//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Star, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { cn } from '../../../lib/utils';
import { useTranslation } from 'react-i18next';
import AiDataService from '../../../utils/AiDataService';
import { useUserAuth } from '../../../UserAuth';
import { useTheme } from '../../../ThemeContext';

type TaskCardProps = {
  id: string;
  title: string;
  description: string;
  icon?: {
    name?: string;
    emoji?: string;
    color?: string;
    data?: string;
  };
  prompt?: string;
  onTitleClick?: (id: string, title: string) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  teamspaceId?: string;
  loopId?: string;
  isFavorite?: boolean;
  onFavoriteChange?: (id: string, isFav: boolean) => void;
};

const TaskCard: React.FC<TaskCardProps> = ({
  id,
  title,
  description,
  icon,
  prompt,
  onTitleClick,
  disabled = false,
  disabledTooltip,
  teamspaceId,
  loopId,
  isFavorite,
  onFavoriteChange,
}) => {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const { user, isAuthenticated } = useUserAuth();
  const aiService = React.useMemo(
    () =>
      new AiDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  // Resolve icon data to a valid <img src> value. Supports raw base64 or full URLs.
  const resolveImageSrc = React.useCallback(
    (ic?: TaskCardProps['icon']): string | undefined => {
      const raw = ic?.data?.trim();
      if (!raw) return undefined;
      // If already a proper data URL, http(s) URL, or root/relative path, use as-is
      if (/^(data:|https?:\/\/|\/|\.\/|\.\.\/)/i.test(raw)) return raw;

      // Infer MIME type from file name when possible; default to PNG
      const name = (ic?.name || '').toLowerCase();
      let mime = 'image/png';
      if (name.endsWith('.svg')) mime = 'image/svg+xml';
      else if (name.endsWith('.jpg') || name.endsWith('.jpeg'))
        mime = 'image/jpeg';
      else if (name.endsWith('.gif')) mime = 'image/gif';

      // Treat incoming string as base64 payload
      return `data:${mime};base64,${raw}`;
    },
    [],
  );

  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [fav, setFav] = React.useState(!!isFavorite);
  React.useEffect(() => {
    setFav(!!isFavorite);
  }, [isFavorite]);
  React.useEffect(() => {
    if (!detailsOpen && expanded) {
      setExpanded(false);
    }
  }, [detailsOpen, expanded]);

  const handleRunTask = React.useCallback(() => {
    if (disabled) return;
    onTitleClick?.(id, title);
  }, [disabled, id, onTitleClick, title]);

  const collapsedActionLabel = t('details', { defaultValue: 'Details' });
  const collapseLabel = t('hide-details', { defaultValue: 'Hide Details' });
  const runLabel = t('task-run');

  return (
    <Card
      className={cn(
        'mb-3 w-full rounded-[14px] border border-[#E2E8F0] bg-gradient-to-br from-[#F9FBFF] to-white shadow-[0px_8px_24px_rgba(15,23,42,0.08)] transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-px hover:border-[#BFDBFE] hover:shadow-[0px_12px_32px_rgba(15,23,42,0.12)]',
        darkMode &&
          'border-[#3f3f3f] bg-gradient-to-br from-[#2d2d2d] to-[#1e1e1e] hover:border-[#5a5a5a] hover:shadow-[0px_12px_32px_rgba(0,0,0,0.3)]',
      )}
      data-testid="id-ai-task-card"
    >
      <CardContent className="px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {(() => {
              const imgSrc = resolveImageSrc(icon);
              if (imgSrc) {
                return (
                  <img
                    src={imgSrc}
                    alt={icon?.name || t('alt-task-icon')}
                    style={{ width: 20, height: 20 }}
                  />
                );
              }
              if (icon?.emoji) {
                return (
                  <span
                    className="text-lg leading-6"
                    aria-label={icon?.name || t('alt-task-icon')}
                  >
                    {icon.emoji}
                  </span>
                );
              }
              return (
                <span
                  className="text-lg leading-6"
                  aria-label={icon?.name || t('alt-task-icon')}
                >
                  ✨
                </span>
              );
            })()}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('activate-task')}
                    className={cn(
                      'font-[Roboto] text-base font-medium leading-6 text-[#354958] no-underline hover:opacity-85',
                      disabled
                        ? 'cursor-not-allowed opacity-60 hover:opacity-60'
                        : 'cursor-pointer',
                      darkMode && 'text-[#e0e0e0]',
                    )}
                    onClick={() => {
                      if (disabled) return;
                      onTitleClick?.(id, title);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (disabled) return;
                        onTitleClick?.(id, title);
                      }
                    }}
                  >
                    {title}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {disabled
                    ? disabledTooltip || t('task-in-progress')
                    : t('click-activate-task')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center gap-2">
            {detailsOpen ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRunTask}
                disabled={disabled}
                className="rounded-full border-[#CBD5F5] bg-[#F8FAFF] font-[Inter] text-[13px] font-medium normal-case text-[#0F172A] hover:border-[#93C5FD] hover:bg-[#EEF4FF] disabled:border-[#E2E8F0] disabled:text-[#94A3B8] disabled:opacity-50"
              >
                {runLabel}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDetailsOpen(true)}
                className="h-auto gap-1 p-0 font-[Inter] text-[13px] font-medium normal-case text-[#2563EB] hover:bg-transparent hover:text-[#1D4ED8]"
              >
                {collapsedActionLabel}
                <ChevronRight
                  aria-label={t('toggle-task-details')}
                  className="size-4 transition-transform duration-200"
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('toggle-favorite')}
              className="size-7"
              onClick={async () => {
                if (disabled) return;
                const next = !fav;
                setFav(next);
                try {
                  if (next) {
                    await aiService.favouriteTask(
                      teamspaceId || '',
                      loopId || '',
                      id,
                    );
                  } else {
                    await aiService.unfavouriteTask(
                      teamspaceId || '',
                      loopId || '',
                      id,
                    );
                  }
                  onFavoriteChange?.(id, next);
                } catch (err) {
                  console.error('[TaskCard] Failed to update favorite', err);
                }
              }}
            >
              {fav ? (
                <Star
                  data-testid="TaskFavIconSelected"
                  aria-hidden="true"
                  className="size-5 fill-[#6FAFFD] text-[#6FAFFD]"
                />
              ) : (
                <Star
                  data-testid="TaskFavIconUnselected"
                  aria-hidden="true"
                  className="size-5 text-[#717182]"
                />
              )}
            </Button>
          </div>
        </div>
        {detailsOpen ? (
          <>
            <span
              className={cn(
                'mt-3 block font-[Roboto] text-xs leading-5 text-[#4A4A56]',
                darkMode && 'text-[#b0b0b0]',
              )}
            >
              {description}
            </span>
            <div className="mt-3 flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded((v) => !v)}
                className="h-auto gap-1 p-0 font-[Inter] text-xs font-medium normal-case text-[#64748B] hover:bg-transparent hover:text-[#475569]"
              >
                {expanded ? t('hide-task-details') : t('view-task-details')}
                <ChevronRight
                  aria-label={t('toggle-task-details')}
                  className={cn(
                    'size-4 transition-transform duration-200',
                    expanded && 'rotate-90',
                  )}
                />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDetailsOpen(false)}
                aria-expanded={detailsOpen}
                className="h-auto gap-1 p-0 font-[Inter] text-xs font-medium normal-case text-[#64748B] hover:bg-transparent hover:text-[#475569]"
              >
                {collapseLabel}
                <ChevronRight
                  aria-label={t('toggle-task-details')}
                  className="size-4 -rotate-90"
                />
              </Button>
            </div>
            <Collapsible open={expanded}>
              <CollapsibleContent>
                <div
                  className={cn(
                    'mt-3 max-h-[360px] overflow-y-auto break-words rounded-[10px] border border-[#E2E8F0] bg-white p-3 font-[Roboto] text-xs leading-5',
                    darkMode && 'border-[#3f3f3f] bg-[#1a1a1a] text-[#d0d0d0]',
                  )}
                >
                  {prompt && <ReactMarkdown>{prompt}</ReactMarkdown>}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TaskCard;
